import { combineReducers } from 'redux';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { InvoiceServiceTarifRow, InvoiceViewVO } from 'generated/bslEbill/invoiceDetail/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { GetInvoiceDetailDataPayload } from 'models/Billing';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { selectProperty } from 'saga/property/ducks';

/* STATE */
export interface InvoiceDetailState {
  invoiceDetail: InvoiceViewVO;
  invoiceDetailLazyload: InvoiceViewVO;
  invoiceServiceRows: InvoiceServiceTarifRow[];
  loadingStatus: LoadingStatus;
  lazyloadIndicator: boolean;
}

/* ACTION TYPES */
export enum InvoiceDetailStateTypes {
  GetInvoiceDetail = '@@InvoiceDetail/GET_INVOICE_DETAIL',
  GetLazyload = '@@InvoiceDetail/GET_LAZYLOAD',
  SetLoading = '@@InvoiceDetail/SET_LOADING',
}

/* ACTIONS */
export const getInvoiceDetailActions = createApiActionCreators(InvoiceDetailStateTypes.GetInvoiceDetail);
export const getInvoiceDetailLazyloadActions = createApiActionCreators(InvoiceDetailStateTypes.GetLazyload);
export const setLoadingAction = createActionCreator(InvoiceDetailStateTypes.SetLoading);

/* REDUCERS */
const initialState: InvoiceDetailState = {
  lazyloadIndicator: false,
  invoiceDetail: {},
  invoiceDetailLazyload: {},
  invoiceServiceRows: [],
  loadingStatus: LoadingStatus.loading,
};

const invoiceDetail = createReducer(initialState.invoiceDetail, {
  [InvoiceDetailStateTypes.GetInvoiceDetail]: {
    [RequestActionTypes.SUCCESS]: (state: InvoiceViewVO, payload: InvoiceViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
});

const lazyloadIndicator = createReducer(initialState.lazyloadIndicator, {
  [InvoiceDetailStateTypes.GetInvoiceDetail]: {
    [RequestActionTypes.SUCCESS]: (state: InvoiceViewVO, payload: InvoiceViewVO) => payload.lazyLoadIndicator,
    [RequestActionTypes.FAILURE]: () => false,
  },
  [InvoiceDetailStateTypes.GetLazyload]: {
    [RequestActionTypes.SUCCESS]: (state: InvoiceViewVO, payload: InvoiceViewVO) => payload.lazyLoadIndicator,
    [RequestActionTypes.FAILURE]: () => false,
  },
});

const invoiceDetailLazyload = createReducer(initialState.invoiceDetailLazyload, {
  [InvoiceDetailStateTypes.GetLazyload]: {
    [RequestActionTypes.SUCCESS]: (state: InvoiceViewVO, payload: InvoiceViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const invoiceServiceRows = createReducer(initialState.invoiceServiceRows, {
  [InvoiceDetailStateTypes.GetInvoiceDetail]: {
    [RequestActionTypes.SUCCESS]: (_state: InvoiceServiceTarifRow[], payload: InvoiceViewVO) => [
      ..._state,
      ...(payload.serviceRows || []),
    ],
    [RequestActionTypes.FAILURE]: () => [],
    [RequestActionTypes.REQUEST]: () => [],
  },
  [InvoiceDetailStateTypes.GetLazyload]: {
    [RequestActionTypes.SUCCESS]: (_state: InvoiceServiceTarifRow[], payload: InvoiceViewVO) => [
      ..._state,
      ...(payload.serviceRows || []),
    ],
    [RequestActionTypes.FAILURE]: (_state: InvoiceServiceTarifRow[]) => [..._state],
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [InvoiceDetailStateTypes.GetInvoiceDetail]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
    [InvoiceDetailStateTypes.GetLazyload]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [InvoiceDetailStateTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

export default combineReducers<InvoiceDetailState>({
  invoiceDetail,
  invoiceDetailLazyload,
  lazyloadIndicator,
  invoiceServiceRows,
  loadingStatus,
});

/* SELECTORS*/
export const selectInvoiceDetail = (state: AppState): InvoiceViewVO => state.invoiceDetail?.invoiceDetail;
export const selectInvoiceDetailLazyload = (state: AppState): InvoiceViewVO =>
  state.invoiceDetail.invoiceDetailLazyload;
export const selectInvoiceServiceRows = (state: AppState): InvoiceServiceTarifRow[] =>
  state.invoiceDetail.invoiceServiceRows;
export const selectInvoiceDetailLoading = (state: AppState): LoadingStatus => state.invoiceDetail.loadingStatus;
export const selectLazyloadIndicator = (state: AppState): boolean => state.invoiceDetail.lazyloadIndicator;

/* SAGAS */
function* getInvoiceDetail({ payload }: AppAction<GetInvoiceDetailDataPayload>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.getData,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.productNo
    ),
    suppressError: true,
  };

  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getInvoiceDetailActions.success(resp.data));
  } else {
    if (resp.status === 404) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID0901' }));
    }

    yield put(getInvoiceDetailActions.failure());
  }
}

function* getInvoiceDetailLazyload({ payload }: AppAction<GetInvoiceDetailDataPayload>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const pageSize = Number(selectProperty('gui.billing.monthDetail.interactiveInvoice.pageSize'));
  const serviceRows: InvoiceServiceTarifRow[] = yield select(selectInvoiceServiceRows);
  const request: ApiClientRequest = {
    apiCall: call(
      api.lazyload,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.productNo,
      serviceRows.length + 1,
      pageSize
    ),
  };

  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getInvoiceDetailLazyloadActions.success(resp.data));
  } else {
    yield put(getInvoiceDetailLazyloadActions.failure());
  }
}

/* EXPORT */
export function* invoiceDetailSaga() {
  yield takeLatest(
    createActionType(InvoiceDetailStateTypes.GetInvoiceDetail, RequestActionTypes.REQUEST),
    getInvoiceDetail
  );
  yield takeLatest(
    createActionType(InvoiceDetailStateTypes.GetLazyload, RequestActionTypes.REQUEST),
    getInvoiceDetailLazyload
  );
}
